import { useEffect, useRef, useState } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// assets
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Replaces CheckCircleOutlined
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'; // Replaces GiftOutlined (or GiftIcon)
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'; // Replaces MessageOutlined
import SettingsIcon from '@mui/icons-material/Settings'; // Replaces SettingOutlined

import useAuth from 'hooks/useAuth';
import 'pages/pages.css';
import { AuthApi } from '../../../../apis/authapi';
import DotLoader from '../../../../components/dotLoader/DotLoader';
import { formatINR } from '../../../../helper/formatINR';
import { useAppQuery } from '../../../../hooks/useAppQuery';

// sx styles
const avatarSX = {
    width: 36,
    height: 36,
    fontSize: '1rem'
};

const actionSX = {
    mt: '6px',
    ml: 1,
    top: 'auto',
    right: 'auto',
    alignSelf: 'flex-start',

    transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Wallet() {
    const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const anchorRef = useRef(null);
    const [read, setRead] = useState(2);
    const [open, setOpen] = useState(false);
    const { userData } = useAuth();

    const {
        data: walletResponse,
        isLoading: walletLoad,
        mutate: refetchWalletData
    } = useAppQuery('middleware', AuthApi.url.fetch_parent_balance);

    const walletBalance = walletResponse?.statusCode === 0 ? (walletResponse?.data?.amount ?? 0) : 0;

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const fetchWallet = async () => {
        try {
            // Bypasses the SWR cache and forces an immediate background server refresh
            await refetchWalletData(undefined, { revalidate: true });
        } catch (err) {
            console.error(err);
        }
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={fetchWallet}>
                <IconButton
                    color="secondary"
                    variant="light"
                    sx={(theme) => ({
                        color: 'text.primary',
                        bgcolor: open ? 'grey.100' : 'transparent',
                        ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
                    })}
                    aria-label="open profile"
                    ref={anchorRef}
                    aria-controls={open ? 'profile-grow' : undefined}
                    aria-haspopup="true"
                >
                    <AccountBalanceWalletOutlinedIcon style={{ fontSize: '20px' }} />
                </IconButton>
                <div style={{ marginRight: '8px', fontWeight: '600' }}>Wallet:</div>
                {!walletLoad ? (
                    <div style={{ display: 'flex', alignItems: 'center', letterSpacing: '0.5px' }}>
                        {formatINR(walletBalance)}
                    </div>
                ) : (
                    <DotLoader />
                )}
                {/* <div>₹ 1,827.71</div> */}
            </Box>

            <Popper
                placement={downMD ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
            >
                {({ TransitionProps }) => (
                    <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                        <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    title="Wallet"
                                    elevation={0}
                                    border={false}
                                    content={false}
                                    secondary={
                                        <>
                                            {read > 0 && (
                                                <Tooltip title="Mark as all read">
                                                    <IconButton color="success" size="small" onClick={() => setRead(0)}>
                                                        <CheckCircleOutlineIcon style={{ fontSize: '1.15rem' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </>
                                    }
                                >
                                    <List
                                        component="nav"
                                        sx={{
                                            p: 0,
                                            '& .MuiListItemButton-root': {
                                                py: 0.5,
                                                px: 2,
                                                '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                                                '& .MuiAvatar-root': avatarSX,
                                                '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                                            }
                                        }}
                                    >
                                        <ListItem
                                            component={ListItemButton}
                                            divider
                                            selected={read > 0}
                                            secondaryAction={
                                                <Typography variant="caption" noWrap>
                                                    3:00 AM
                                                </Typography>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ color: 'success.main', bgcolor: 'success.lighter' }}>
                                                    <CardGiftcardIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h6">
                                                        It&apos;s{' '}
                                                        <Typography component="span" variant="subtitle1">
                                                            Cristina danny&apos;s
                                                        </Typography>{' '}
                                                        birthday today.
                                                    </Typography>
                                                }
                                                secondary="2 min ago"
                                            />
                                        </ListItem>
                                        <ListItem
                                            component={ListItemButton}
                                            divider
                                            secondaryAction={
                                                <Typography variant="caption" noWrap>
                                                    6:00 AM
                                                </Typography>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>
                                                    <ChatBubbleOutlineIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h6">
                                                        <Typography component="span" variant="subtitle1">
                                                            Aida Burg
                                                        </Typography>{' '}
                                                        commented your post.
                                                    </Typography>
                                                }
                                                secondary="5 August"
                                            />
                                        </ListItem>
                                        <ListItem
                                            component={ListItemButton}
                                            divider
                                            selected={read > 0}
                                            secondaryAction={
                                                <Typography variant="caption" noWrap>
                                                    2:45 PM
                                                </Typography>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ color: 'error.main', bgcolor: 'error.lighter' }}>
                                                    <SettingsIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h6">
                                                        Your Profile is Complete &nbsp;
                                                        <Typography component="span" variant="subtitle1">
                                                            60%
                                                        </Typography>{' '}
                                                    </Typography>
                                                }
                                                secondary="7 hours ago"
                                            />
                                        </ListItem>
                                        <ListItem
                                            component={ListItemButton}
                                            divider
                                            secondaryAction={
                                                <Typography variant="caption" noWrap>
                                                    9:10 PM
                                                </Typography>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>C</Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h6">
                                                        <Typography component="span" variant="subtitle1">
                                                            Cristina Danny
                                                        </Typography>{' '}
                                                        invited to join{' '}
                                                        <Typography component="span" variant="subtitle1">
                                                            Meeting.
                                                        </Typography>
                                                    </Typography>
                                                }
                                                secondary="Daily scrum meeting time"
                                            />
                                        </ListItem>
                                        <ListItemButton sx={{ textAlign: 'center', py: `${12}px !important` }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h6" color="primary">
                                                        View All
                                                    </Typography>
                                                }
                                            />
                                        </ListItemButton>
                                    </List>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </Box>
    );
}
